import { Pages, routeBuilders } from './routes/routesState'
import { CmsPageState } from './cmsPage/cmsPageState'
import { NewsOverviewState } from './newsOverview/newsOverviewState'
import { NewsAndEventsOverviewState } from './newsAndEventsOverview/newsAndEventsOverviewState'
import { SpecialistOverviewState } from './specialistOverview/specialistOverviewState'
import { DepartmentOrCentrumOverviewState } from './departmentsAndCentraOverview/departmentsAndCentraOverviewState'
import { VerpleegafdelingenOverviewState } from './verpleegafdelingenOverview/verpleegafdelingenOverviewState'
import { FooterState, initialFooterState } from './footer/footerState'
import { EventOverviewState } from './eventOverview/eventOverviewState'
import { NotFoundState } from './notFound/notFoundState'
import { CookiebarState, initialCookiebarState } from './cookiebar/cookiebarState'
import { SearchPageState } from './searchPage/searchPageState'
import { HeaderState, initialHeaderState } from './header/headerState'
import { initialViewListenerState, ViewListenerState } from './viewListener/viewListenerState'
import { FoldersState } from './folders/foldersState'
import { ResearchOverviewState } from './researchOverview/researchOverviewState'
import { CrisisBannerState, initialCrisisBannerState } from './crisisbanner/crisisbannerState'
import { AsyncState, Updater, initialRoute, loadingAsyncState } from 'widgets-for-react'
import { getNavigationItems } from './cmsPage/page/departmentPage/departmentPageApi'
import { CurrentPage } from '../utils/routes.shared'
import { routesArray } from './routes/routesWidget'

export type RootState = {
  view: 'mobile' | 'desktop'
  page: CurrentPage<Pages>
  cookiebarState: CookiebarState
  headerState: HeaderState
  footerState: FooterState
  crisisBannerState: CrisisBannerState
  // Load these once, since they don't change per page
  navigationItems: AsyncState<string[]>
}


export const initialRootState = () => {
  const initialRootState: RootState = {
    view: initialViewListenerState,
    page: routeBuilders.notFound.make({}),
    cookiebarState: initialCookiebarState(),
    headerState: initialHeaderState,
    footerState: initialFooterState,
    crisisBannerState: initialCrisisBannerState,
    navigationItems: loadingAsyncState(getNavigationItems)
  }
  const updater = initialRoute(routesArray([]))
  return updater === undefined ? initialRootState : updater(initialRootState)
}

export const rootStateUpdaters = {
  updateViewListenerState:
    (update: ViewListenerState) =>
    (state: RootState): RootState => ({
      ...state,
      view: update,
    }),

  updateCookiebarState:
    (updater: Updater<CookiebarState>) =>
    (state: RootState): RootState => ({
      ...state,
      cookiebarState: updater(state.cookiebarState),
    }),

  updateHeaderState:
    (updater: Updater<HeaderState>) =>
    (state: RootState): RootState => ({
      ...state,
      headerState: updater(state.headerState),
    }),

  updateFooterState:
    (updater: Updater<FooterState>) =>
    (state: RootState): RootState => ({
      ...state,
      footerState: updater(state.footerState),
    }),

  updateCrisisBannerState:
    (updater: Updater<CrisisBannerState>) =>
    (state: RootState): RootState => ({
      ...state,
      crisisBannerState: updater(state.crisisBannerState),
    }),

  updateNotFoundPageState:
    (updater: Updater<NotFoundState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'notFound'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateCmsPageState:
    (updater: Updater<CmsPageState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'home' ||
        state.page.kind === 'englishHome' ||
        state.page.kind === 'spaarnePrimary' ||
        state.page.kind === 'spaarneSecondary' ||
        state.page.kind === 'spaarneTernary' ||
        state.page.kind === 'englishSpaarnePrimary' ||
        state.page.kind === 'englishSpaarneSecondary' ||
        state.page.kind === 'englishSpaarneTernary' ||
        state.page.kind === 'friend' ||
        state.page.kind === 'friendHome' ||
        state.page.kind === 'departmentOrCentrum' ||
        state.page.kind === 'subDepartmentOrDepartmentOrSubCentrumOrSpaarne' ||
        state.page.kind === 'subDepartmentOrSpaarne' ||
        state.page.kind === 'news' ||
        state.page.kind === 'event' ||
        state.page.kind === 'dossierPage' ||
        state.page.kind === 'dossierChildPage' ||
        state.page.kind === 'locationOrSpaarne' ||
        state.page.kind === 'specialist' ||
        state.page.kind === 'waitingTimes' ||
        state.page.kind === 'folderPage' ||
        state.page.kind === 'research' ||
        state.page.kind === 'preview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateEventOverviewState:
    (updater: Updater<EventOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'eventOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateDepartmentOverviewState:
    (updater: Updater<DepartmentOrCentrumOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'departmentsAndCentraOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateVerpleegafdelingenOverviewState:
    (updater: Updater<VerpleegafdelingenOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'verpleegafdelingenOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateNewsOverviewState:
    (updater: Updater<NewsOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'newsOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateNewsAndEventsOverviewState:
    (updater: Updater<NewsAndEventsOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'newsAndEventsOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateSpecialistOverviewState:
    (updater: Updater<SpecialistOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'specialistOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateSearchPage:
    (updater: Updater<SearchPageState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'search' || state.page.kind == 'englishSearch'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateFoldersPage:
    (updater: Updater<FoldersState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'folders'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateResearchOverviewState:
    (updater: Updater<ResearchOverviewState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'researchOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  navigationItemsUpdater:
    (updater: Updater<AsyncState<string[]>>) =>
    (state: RootState): RootState => ({
      ...state,
      navigationItems: updater(state.navigationItems)
    }),
}
